/**
 * The logic of deleting an account, kept apart from the dialogs so it can be tested.
 *
 * The flow is: explain the 30-day grace period → type DELETE and the password → the server schedules it
 * → sign out. Accounts without a password (Google/Apple) leave it empty and must have signed in within
 * the last 10 minutes; the server says so when they have not.
 * Signing back in before the date shows a blocking prompt that offers to cancel.
 */
import type { AccountDeletionState } from '@api/endpoints/accountDeletion'

export const DELETE_CONFIRMATION = 'DELETE' as const
export const DELETION_GRACE_DAYS = 30

export interface PendingDeletion {
  requestedAt: string
  purgeAfter: string
}

/** Exactly DELETE, in capitals. Surrounding spaces are forgiven; anything else is not. */
export function isDeleteConfirmation(text: string): boolean {
  return text.trim() === DELETE_CONFIRMATION
}

/**
 * A scheduled deletion from either source: the deletion endpoint's state or the `pendingDeletion`
 * field the account payload carries. Anything incomplete counts as none, because the prompt it
 * drives is blocking and must never appear for a deletion that is not real.
 */
export function pendingDeletionFrom(
  source: AccountDeletionState | { pendingDeletion?: Partial<PendingDeletion> | null } | null | undefined,
): PendingDeletion | null {
  if (!source) return null
  if ('status' in source) {
    if (source.status !== 'scheduled' || !source.purgeAfter) return null
    return { requestedAt: source.requestedAt ?? '', purgeAfter: source.purgeAfter }
  }
  const pending = 'pendingDeletion' in source ? source.pendingDeletion : null
  if (!pending?.purgeAfter) return null
  return { requestedAt: pending.requestedAt ?? '', purgeAfter: pending.purgeAfter }
}

/** "October 12, 2026" — the date the account goes, in the reader's own locale. */
export function formatDeletionDate(iso: string, locale?: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
}

/** Whole days left until the purge, never negative. */
export function daysUntil(iso: string, now: Date = new Date()): number {
  const ms = new Date(iso).getTime() - now.getTime()
  if (Number.isNaN(ms)) return 0
  return Math.max(0, Math.ceil(ms / 86_400_000))
}

// ---------------------------------------------------------------------------------------------
// The delete dialog's steps
// ---------------------------------------------------------------------------------------------

export type DeleteFlowStep = 'explain' | 'confirm' | 'submitting' | 'scheduled' | 'error'

export interface DeleteFlowState {
  step: DeleteFlowStep
  confirmation: string
  /** The account's password. The server needs it; Google/Apple accounts leave it empty. */
  password: string
  reason: string
  error: string | null
  result: PendingDeletion | null
}

export type DeleteFlowAction =
  | { type: 'continue' }
  | { type: 'back' }
  | { type: 'typeConfirmation'; value: string }
  | { type: 'typePassword'; value: string }
  | { type: 'typeReason'; value: string }
  | { type: 'submit' }
  | { type: 'succeeded'; result: PendingDeletion }
  | { type: 'failed'; message: string }
  | { type: 'reset' }

export const initialDeleteFlow: DeleteFlowState = {
  step: 'explain',
  confirmation: '',
  password: '',
  reason: '',
  error: null,
  result: null,
}

export function deleteFlowReducer(state: DeleteFlowState, action: DeleteFlowAction): DeleteFlowState {
  switch (action.type) {
    case 'continue':
      return state.step === 'explain' ? { ...state, step: 'confirm' } : state
    case 'back':
      return state.step === 'confirm' || state.step === 'error'
        ? { ...state, step: state.step === 'error' ? 'confirm' : 'explain', error: null }
        : state
    case 'typeConfirmation':
      return { ...state, confirmation: action.value }
    case 'typePassword':
      return { ...state, password: action.value }
    case 'typeReason':
      return { ...state, reason: action.value }
    case 'submit':
      // Only from the confirm step, and only with DELETE typed; a double click submits once.
      if ((state.step !== 'confirm' && state.step !== 'error') || !isDeleteConfirmation(state.confirmation))
        return state
      return { ...state, step: 'submitting', error: null }
    case 'succeeded':
      return state.step === 'submitting' ? { ...state, step: 'scheduled', result: action.result } : state
    case 'failed':
      return state.step === 'submitting' ? { ...state, step: 'error', error: action.message } : state
    case 'reset':
      return initialDeleteFlow
    default:
      return state
  }
}

/** The request body the server expects, with an empty reason or password left out. */
export function deletionRequestBody(state: Pick<DeleteFlowState, 'reason' | 'password'>): {
  confirmation: typeof DELETE_CONFIRMATION
  currentPassword?: string
  reason?: string
} {
  const reason = state.reason.trim()
  return {
    confirmation: DELETE_CONFIRMATION,
    ...(state.password ? { currentPassword: state.password } : {}),
    ...(reason ? { reason } : {}),
  }
}

// ---------------------------------------------------------------------------------------------
// The prompt on sign-in
// ---------------------------------------------------------------------------------------------

/**
 * "Continue" on the prompt is remembered for this browser tab only, and only for the deletion it
 * was answered for. A new tab, a new sign-in or a rescheduled deletion asks again.
 */
export function pendingDeletionDismissKey(pending: PendingDeletion): string {
  return `adventurecore-deletion-continue:${pending.purgeAfter}`
}

export function shouldPromptForPendingDeletion(
  pending: PendingDeletion | null,
  dismissed: (key: string) => boolean,
): boolean {
  if (!pending) return false
  return !dismissed(pendingDeletionDismissKey(pending))
}
